import { useEffect, useState } from 'react'
import { HelpCircle, X } from 'lucide-react'
import { submitSupportTicket } from '../api/client'
import { getRecentErrors } from '../lib/errorBuffer'
import { getPageState } from '../lib/pageState'

const DRAFT_KEY = 'support_ticket_draft'

function captureBrowserInfo() {
  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    viewport: { w: window.innerWidth, h: window.innerHeight },
  }
}

interface CapturedContext {
  errors: ReturnType<typeof getRecentErrors>
  browser: ReturnType<typeof captureBrowserInfo>
  pageState: ReturnType<typeof getPageState>
  url: string
}

function captureNow(): CapturedContext {
  return {
    errors: getRecentErrors(),
    browser: captureBrowserInfo(),
    pageState: getPageState(),
    url: window.location.href,
  }
}

export default function HelpButton() {
  const [open, setOpen] = useState(false)
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showContext, setShowContext] = useState(false)
  const [context, setContext] = useState<CapturedContext | null>(null)

  // Capture context at modal-open time so what the user sees in the preview
  // matches what gets submitted, even if errors fire while the modal is open.
  useEffect(() => {
    if (open) {
      setContext(captureNow())
      const draft = sessionStorage.getItem(DRAFT_KEY)
      if (draft) {
        setDescription(draft)
        sessionStorage.removeItem(DRAFT_KEY)
      }
    }
  }, [open])

  async function handleSubmit() {
    if (!description.trim() || submitting || !context) return
    setSubmitting(true)
    setError(null)
    try {
      await submitSupportTicket({
        description: description.trim(),
        url: context.url,
        page_state: context.pageState as unknown as Record<string, unknown>,
        console_errors: context.errors,
        browser_info: context.browser,
      })
      setDescription('')
      setOpen(false)
      console.info('[support] ticket submitted')
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 401) {
        sessionStorage.setItem(DRAFT_KEY, description)
        return
      }
      setError("Couldn't submit — check connection or try again.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Report a problem"
        className="fixed bottom-5 right-5 z-50 h-12 w-12 rounded-full bg-[#EA580C] text-white shadow-lg flex items-center justify-center hover:bg-[#C2410C] transition-colors"
      >
        <HelpCircle size={22} strokeWidth={1.75} />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => setOpen(false)}
        >
          <div
            className="w-full max-w-md bg-[#1C1412] border border-[#2E2420] rounded-lg p-5 text-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display tracking-widest uppercase text-sm">Report a problem</h2>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="text-[#A8998E] hover:text-white"
              >
                <X size={18} />
              </button>
            </div>

            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What broke or didn't behave the way you expected?"
              className="w-full h-28 bg-[#0F0908] border border-[#2E2420] rounded-md p-3 text-sm text-white placeholder:text-[#6B5E54] focus:outline-none focus:border-[#EA580C]"
              disabled={submitting}
            />

            <button
              type="button"
              onClick={() => setShowContext(s => !s)}
              className="mt-2 text-[0.7rem] text-[#A8998E] hover:text-white tracking-widest uppercase"
            >
              {showContext ? 'Hide' : 'Show'} context being sent
            </button>

            {showContext && context && (
              <pre className="mt-2 text-[0.65rem] text-[#A8998E] bg-[#0F0908] border border-[#2E2420] rounded-md p-2 max-h-40 overflow-auto whitespace-pre-wrap">
{JSON.stringify({ url: context.url, page_state: context.pageState, browser: context.browser, console_errors: context.errors }, null, 2)}
              </pre>
            )}

            {error && <p className="mt-2 text-xs text-red-400">{error}</p>}

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="px-3 py-1.5 text-xs text-[#A8998E] hover:text-white"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!description.trim() || submitting}
                className="px-4 py-1.5 bg-[#EA580C] hover:bg-[#C2410C] disabled:bg-[#3a2418] disabled:text-[#6B5E54] text-white text-xs rounded-md tracking-widest uppercase"
              >
                {submitting ? 'Submitting…' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
